import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { edgeFromRelationship, makeMeta, nodeFromPerson } from '@/features/graph/utils/builders'
import type { GraphData } from '@/services/api/graph'
import { graphApi } from '@/services/api/graph'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'

export interface EvidenceGraphOptions {
  /** The small graph the API answers for one piece of the person's evidence. */
  fetch: (evidenceId: string) => Promise<GraphData>
  /** The i18n key of the contribution's label on the canvas. */
  label: string
  /** What the view shows of the nodes beyond their presence; nothing special by default. */
  view?: (graph: GraphData) => GraphView['view']
}

/** Add only the chosen evidence to the current investigation, preserving its contents, and open the graph. */
export function useEvidenceGraph(caseId: string, options: EvidenceGraphOptions) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async (evidenceId: string) => {
      const graph = await options.fetch(evidenceId)
      const label = t(options.label)
      const view = {
        meta: makeMeta(caseId, label),
        nodes: graph.nodes.map(nodeFromPerson),
        edges: graph.relationships.map(edgeFromRelationship),
        view: options.view?.(graph) ?? emptyViewState(),
      }
      getOrCreateInvestigationsStore({ caseId }).getState().addToObjectSet(view, label, 'expand')
    },
    onSuccess: () => navigate(`/cases/${caseId}/graph`),
    onError: () => toast.error(t('investigations.graph.faces.graphError')),
  })
}

/** A confirmed face of the person on the canvas, with the face expanded. */
export function useFaceEvidenceGraph(caseId: string, personId: string | null) {
  return useEvidenceGraph(caseId, {
    fetch: faceId => {
      if (!personId) throw new Error('Identity is required')
      return graphApi.getFaceEvidence(caseId, personId, faceId)
    },
    label: 'investigations.graph.faces.graphEvidence',
    view: graph => ({
      ...emptyViewState(),
      expandedFaceIds: graph.nodes
        .filter(node => node.labels.includes('Face'))
        .map(node => node.id),
    }),
  })
}
